import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase, initializeAnonAuth, fetchNovaConfig, submitVote, fetchSiteConfig, fetchActivePoll } from './client';

/**
 * Custom hook for Supabase integration
 * Handles anonymous authentication and fetching Nova configuration
 */
export const useSupabase = () => {
  const [searchParams] = useSearchParams(); // Get query parameters
  const id = searchParams.get('id'); // Get the id from query params
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pollConfig, setPollConfig] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);
  const [error, setError] = useState(null);
  
  // Initialize and fetch configuration in a single effect
  useEffect(() => {
    const initializeApp = async () => {
      setIsLoading(true);
      try {
        // Step 1: Initialize anonymous authentication
        console.log('Initializing anonymous authentication...');
        const authResult = await initializeAnonAuth();
        
        if (!authResult.success) {
          console.error('Authentication failed:', authResult.error);
          setError({
            message: 'Authentication failed',
            details: authResult.error?.message || 'Could not authenticate with Supabase',
            code: 'AUTH_ERROR'
          });
          setIsLoading(false);
          return;
        }
        
        setIsAuthenticated(true);
        console.log('Authentication successful');

        // Step 2: Fetch site configuration
        console.log('Fetching site configuration...');
        const siteConfigResult = await fetchSiteConfig();
        if (siteConfigResult.success) {
          setSiteConfig(siteConfigResult.data);
          console.log('Site config loaded:', siteConfigResult.data);
        } else {
          console.error('Error loading site config:', siteConfigResult.error);
          setError({
            message: 'Failed to load site configuration',
            details: siteConfigResult.error?.message || 'Unknown error',
            code: 'SITE_CONFIG_ERROR'
          });
          setIsLoading(false);
          return;
        }
        
        // Step 3: Get id from route params and fetch configuration
        if (id) {
          console.log('Fetching Nova config for id:', id);
          // Pass the session from authentication to fetchNovaConfig
          const { success, data, error } = await fetchNovaConfig(id, authResult.session);
          
          if (success && data) {
            setPollConfig(data);
            console.log('Nova config loaded:', data);
          } else if (error) {
            console.error('Error fetching Nova config:', error);
            setError({
              message: `Failed to load configuration for id: ${id}`,
              details: error.message || 'Unknown error',
              code: error.code || 'CONFIG_FETCH_ERROR'
            });
          }
        } else {
          console.log('No id parameter found in route, fetching active poll...');
          // If no ID is provided, fetch the currently active poll
          const activePollResult = await fetchActivePoll(authResult.session);
          
          if (activePollResult.success && activePollResult.data) {
            setPollConfig(activePollResult.data);
            console.log('Active poll loaded:', activePollResult.data);
          } else {
            console.error('Error fetching active poll:', activePollResult.error);
            setError({
              message: 'Failed to load active poll',
              details: activePollResult.error?.message || 'No active polls found',
              code: 'NO_ACTIVE_POLL'
            });
          }
        }
      } catch (err) {
        console.error('Error during app initialization:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeApp();
  }, []);
  
  /**
   * Manually fetch Nova config with a specific slug
   * @param {string} slug - The slug to fetch configuration for
   */
  const getNovaConfig = async (slug) => {
    setIsLoading(true);
    try {
      const result = await fetchNovaConfig(slug);
      if (result.success) {
        setPollConfig(result.data);
      } else {
        setError(result.error);
      }
      return result;
    } catch (err) {
      setError(err);
      return { success: false, error: err };
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Submit a vote for a contestant
   * @param {Object} voteData - The vote data to submit
   * @returns {Promise<Object>} - The result of the vote submission
   */
  const submitVoteForContestant = async (voteData) => {
    try {
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('No active session found for vote submission');
        return {
          success: false,
          error: 'Authentication required for voting'
        };
      }
      
      // Prepare vote data with config ID from the current pollConfig
      const preparedVoteData = {
        ...voteData,
        pollId: pollConfig?.id
      };
      
      // Submit the vote using the client function
      const result = await submitVote(preparedVoteData, session);
      return result;
    } catch (error) {
      console.error('Error submitting vote:', error);
      return {
        success: false,
        error: error.message || 'Failed to submit vote'
      };
    }
  };

  return {
    supabase,
    isAuthenticated,
    isLoading,
    pollConfig,
    siteConfig,
    error,
    getNovaConfig,
    submitVoteForContestant
  };
};

export default useSupabase;
